import {
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Button,
  Table,
  Form,
  notification,
  PageHeader,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
} from "antd";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import IconNotFound from "../../static/icon/IconNotFound";
import DeleteAction from "../../Tools/DeleteAction";

import { useAuth } from "../../context/auth";
import MerchantDropdown from "../Dropdown/MerchantDropdown";
import LandlordDropdown from "../Dropdown/LandlordDropdown";
import { formatDateTime, formatPaymentMethod, listPaymentMethod } from "../../utils/formatter";
import IconPayment from "../../static/icon/IconPayment";
import Swal from "sweetalert2";

const Payment = () => {
  const history = useHistory();
  const { accessToken } = useAuth();

  const [form] = Form.useForm();
  const selectedMerchants = Form.useWatch('merchantIds', form);

  const [loading, setLoading] = useState(false);

  const [update, setUpdate] = useState(0);
  const [total, setTotal] = useState(0);
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState({
    pageValue: history.location.pageValue ?? {},
    searchValue: history.location.searchValue ?? {},
  });

  useEffect(() => {
    form.setFieldsValue(search.searchValue);
  }, [form, search]);

  const [currentPayment, setCurrentPayment] = useState(null);

  useEffect(() => {
    async function getPayments({ pageValue, searchValue }) {
      setLoading(true);

      const url = new URL('/api/payment', window.location);
      if (searchValue.invoiceNo) url.searchParams.append('invoiceNo', searchValue.invoiceNo);
      if (searchValue.landlordIds) for (const landlordId of searchValue.landlordIds) url.searchParams.append('landlordId', landlordId);
      if (searchValue.merchantIds) for (const merchantId of searchValue.merchantIds) url.searchParams.append('merchantId', merchantId);
      if (searchValue.bookingId) url.searchParams.append('bookingId', searchValue.bookingId);
      if (searchValue.userName) url.searchParams.append('userName', searchValue.userName);
      if (searchValue.createdAfter) url.searchParams.append('createdAfter', searchValue.createdAfter.startOf('day').toISOString());
      if (searchValue.createdBefore) url.searchParams.append('createdBefore', searchValue.createdBefore.endOf('day').toISOString());
      if (searchValue.paymentMethod) url.searchParams.append('paymentMethod', searchValue.paymentMethod);

      if (pageValue.page) url.searchParams.append('page', pageValue.page);
      if (pageValue.size) url.searchParams.append('size', pageValue.size);

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { total, payments } = await res.json();
        setTotal(total);
        setPayments(payments);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }

      setLoading(false);
    };

    getPayments(search);
  }, [search, accessToken, update]);

  const columns = [
    {
      title: "Invoice No",
      dataIndex: "invoiceNo",
      key: "invoiceNo",
    },
    {
      title: "Name",
      dataIndex: "firstName",
      key: "firstName",
    },
    {
      title: "Surname",
      dataIndex: "lastName",
      key: "lastName",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Cluster Name",
      dataIndex: "merchantNameEn",
      key: "merchantNameEn",
    },
    {
      title: "Site Name",
      dataIndex: "landlordNameEn",
      key: "landlordNameEn",
    },
    {
      title: "Booking Id",
      dataIndex: "bookingId",
      key: "bookingId",
    },
    {
      title: "Payment Date / Time",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => formatDateTime(new Date(createdAt)),
    },
    {
      title: "Payment Method",
      dataIndex: "method",
      key: "method",
      render: (method) => formatPaymentMethod(method),
    },
    {
      title: "Payment Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => `THB ${amount.toFixed(2)}`,
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, payment) => (
        <span class="whitespace-nowrap flex">
          {(!['zero', 'qr'].includes(payment.method)) && (
            <Button
              size="small"
              type="link"
              danger
              disabled={payment.refundedAt}
              onClick={() => {
                Swal.fire({
                  title: `Refund booking`,
                  text: 'Are you sure to refund this booking?',
                  input: 'number',
                  inputLabel: 'Refund amount (THB)',
                  inputValue: payment.amount.toFixed(2),
                  inputValidator: value => isNaN(value) && 'Please input number',
                  showCancelButton: true,
                  confirmButtonText: "Refund",
                  showLoaderOnConfirm: true,
                  reverseButtons: true,
                  async preConfirm(amount) {
                    const url = new URL(`/api/payment/${payment.id}/refund`, window.location);
                    const res = await fetch(url, {
                      headers: { 'X-ACCESS-TOKEN': accessToken },
                      method: 'POST',
                      body: JSON.stringify({ amount: +amount })
                    });
                    if (res.ok) {
                      Swal.fire({
                        icon: "success",
                        title: "Refunded successfully!",
                        showConfirmButton: false,
                        timer: 1500,
                      });
                      setUpdate(update => update + 1);
                    } else {
                      notification["error"]({
                        message: "Something went wrong !",
                        description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
                      });
                    }
                  },
                });
              }}
            >
              Refund
            </Button>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mt-2 space-x-4">
        <PageHeader
          style={{ padding: 0, width: "100%" }}
          title={
            <span className="flex items-center mt-2">
              <IconPayment /> &nbsp;&nbsp;Payment
            </span>
          }
        />
      </div>
      <div className="content">
        <div className="">
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => setSearch(search => ({
              pageValue: { ...search.pageValue, page: 1 },
              searchValue: values,
            }))}
            onReset={() => form.submit()}
          >
            <Row align="middle" gutter={16}>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Invoice No"
                  name="invoiceNo"
                  colon={false}
                  initialValue={null}
                >
                  <Input
                    placeholder="Invoice No"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Name/Email/Tel"
                  name="userName"
                  colon={false}
                  initialValue={null}
                >
                  <Input
                    placeholder="Name/Email/Tel"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={6}>
                <Form.Item
                  label="Cluster Name"
                  name="merchantIds"
                  colon={false}
                  initialValue={[]}
                >
                  <MerchantDropdown
                    allowClear
                    allowSearch
                    mode="multiple"
                    setSelectedMerchants={(merchantIds) => form.setFieldsValue({ merchantIds })}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={6}>
                <Form.Item
                  label="Site Name"
                  name="landlordIds"
                  colon={false}
                  initialValue={[]}
                >
                  <LandlordDropdown
                    allowClear
                    allowSearch
                    mode="multiple"
                    merchantIds={selectedMerchants}
                    setSelectedLandlords={(landlordIds) => form.setFieldsValue({ landlordIds })}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Booking Id"
                  name="bookingId"
                  colon={false}
                  initialValue={null}
                >
                  <Input
                    placeholder="Booking Id"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Payment Method"
                  name="paymentMethod"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    placeholder="Payment Method"
                    allowClear
                    options={[
                      { value: null, label: 'Any Payment Method' },
                      ...listPaymentMethod.map(type => ({
                        value: type.name,
                        label: type.value,
                      }))
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Service Date From"
                  name="createdAfter"
                  colon={false}
                  initialValue={null}
                >
                  <DatePicker
                    placeholder="Service Date From"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Service Date To"
                  name="createdBefore"
                  colon={false}
                  initialValue={null}
                >
                  <DatePicker
                    placeholder="Service Date To"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={24}>
                <div className="flex">
                  <Button
                    className="w-28"
                    type="primary"
                    shape="round"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                  >
                    Search
                  </Button>

                  <Button
                    className="w-28 ml-2"
                    type="primary"
                    ghost
                    shape="round"
                    htmlType="reset"
                    icon={<SyncOutlined />}
                  >
                    Clear Data
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
      <div className="content">
        <Table
          className="inbox_table"
          locale={{ emptyText: <IconNotFound /> }}
          rowKey={(payment) => payment.id}
          loading={loading}
          columns={columns}
          dataSource={payments}
          pagination={{
            size: "small",
            current: search.pageValue.page,
            pageSize: search.pageValue.size,
            defaultPageSize: 10,
            total,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (total, range) => ` ${range[0]}-${range[1]} of ${total} `,
          }}
          onChange={(pagination, _, sorter) => setSearch(search => ({
            pageValue: {
              page: pagination.current ?? 1,
              size: pagination.pageSize ?? 10,
              sort: sorter.columnKey,
              order: sorter.order === 'ascend' ? 'asc' : 'desc',
            },
            searchValue: search.searchValue,
          }))}
        />
      </div>

      <DeleteAction
        openModalActionDelete={!!currentPayment}
        name="Payment"
        type="payment"
        editRecord={currentPayment}
        onCancel={() => setCurrentPayment(null)}
        onOk={() => {
          setCurrentPayment(null);
          setUpdate(update + 1);
        }}
      />
    </>
  );
};

export default Payment;
